/**
 * Teaching the board once, quietly (ADMIN-263): no tour. A tip under the board on the fifth new
 * tab, and the starters the first time Customize opens. Kept per person in `chrome.storage.local`
 * (localStorage on the web app), so it's per browser, like the habit it counts.
 */

export type Onboarding = {
  /** New tabs opened while signed in, capped. */
  visits: number;
  /** "Not now" on the tip (or Customize opened from anywhere): it never comes back. */
  tipDismissed: boolean;
  /** The starters were offered once in Customize. */
  startersOffered: boolean;
};

/**
 * The tip shows from this visit on.
 *
 * @internal exported for tests
 */
export const TIP_FROM_VISIT = 5;
const MAX_VISITS = 1000;

export const freshOnboarding: Onboarding = {
  visits: 0,
  tipDismissed: false,
  startersOffered: false,
};

/** The tip: from the fifth visit, until dismissed, and only while the board is still a given one. */
export function showTip(s: Onboarding, hasOwnBoard: boolean): boolean {
  return !s.tipDismissed && !hasOwnBoard && s.visits >= TIP_FROM_VISIT;
}

/** The starters: once, the first time Customize opens, if this person hasn't arranged a board. */
export function offerStarters(s: Onboarding, hasOwnBoard: boolean): boolean {
  return !s.startersOffered && !hasOwnBoard;
}

export const countVisit = (s: Onboarding): Onboarding => ({
  ...s,
  visits: Math.min(s.visits + 1, MAX_VISITS),
});

/** A stored value, with anything missing or malformed taken as fresh. */
export function parseOnboarding(raw: unknown): Onboarding {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<Onboarding>;
  return {
    visits: typeof r.visits === "number" && r.visits >= 0 ? Math.min(r.visits, MAX_VISITS) : 0,
    tipDismissed: r.tipDismissed === true,
    startersOffered: r.startersOffered === true,
  };
}

export const onboardingKey = (userId: string) => `home-onboarding:${userId}`;

/** Where the state lives: `chrome.storage.local`, or a stand-in in tests and on the web. */
export type OnboardingStore = {
  get: (key: string) => Promise<unknown>;
  set: (key: string, value: Onboarding) => Promise<void>;
};
